import React, { useState } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon, ArrowUpTrayIcon, DocumentTextIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Papa from 'papaparse';
import ApiClient from '../../utils/ApiClient';

export default function BulkUpdateModal({ open, onClose, onComplete }) {
    const [csvFile, setCsvFile] = useState(null);
    const [parsedData, setParsedData] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [dragCounter, setDragCounter] = useState(0);
    const [isProcessing, setIsProcessing] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [previewData, setPreviewData] = useState([]);

    const requiredColumns = ['upc', 'MAP', 'item_code', 'product_title', 'cost'];

    const preventDefaults = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragEnter = (e) => {
        preventDefaults(e);
        setDragCounter(prev => prev + 1);
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        preventDefaults(e);
        setDragCounter(prev => prev - 1);
        if (dragCounter === 1) {
            setIsDragging(false);
        }
    };

    const handleDrop = (e) => {
        preventDefaults(e);
        setIsDragging(false);
        setDragCounter(0);
        const files = Array.from(e.dataTransfer.files);
        handleFiles(files);
    };

    const handleFileInput = (e) => {
        const files = Array.from(e.target.files);
        handleFiles(files);
    };

    const handleFiles = async (files) => {
        const csvFiles = files.filter(
            (file) => file.type === 'text/csv' || file.name.endsWith('.csv') || file.name.endsWith('.txt')
        );
        
        if (csvFiles.length === 0) {
            alert('Please upload a valid CSV file.');
            return;
        }

        setCsvFile(csvFiles[0]);
        await parseFile(csvFiles[0]);
    };

    const parseFile = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const text = event.target.result;
                Papa.parse(text, {
                    header: true,
                    skipEmptyLines: 'greedy',
                    dynamicTyping: false,
                    transformHeader: (header) => header.trim(),
                    complete: function(results) {
                        if (results.errors.length > 0) {
                            alert('Error parsing CSV: ' + results.errors[0].message);
                            reject(new Error('Error parsing CSV: ' + results.errors[0].message));
                            return;
                        }

                        // Validate required columns
                        const headers = results.meta.fields || [];
                        const missingColumns = requiredColumns.filter(col => !headers.includes(col));
                        
                        if (missingColumns.length > 0) {
                            alert(`Missing required columns: ${missingColumns.join(', ')}`);
                            reject(new Error(`Missing required columns: ${missingColumns.join(', ')}`));
                            return;
                        }

                        const transformedData = results.data.map(row => {
                            const newRow = {};
                            Object.entries(row).forEach(([key, value]) => {
                                const trimmedValue = typeof value === 'string' ? value.trim() : value;
                                newRow[key] = trimmedValue;
                            });
                            return newRow;
                        });

                        setParsedData(transformedData);
                        setPreviewData(transformedData.slice(0, 5)); // Show first 5 rows for preview
                        resolve(transformedData);
                    },
                    error: function(error) {
                        alert('Error parsing CSV file: ' + error.message);
                        reject(error);
                    }
                });
            };
            reader.onerror = () => {
                alert('Error reading file');
                reject(reader.error);
            };
            reader.readAsText(file);
        });
    };

    const handleSubmit = async () => {
        if (!parsedData || parsedData.length === 0) {
            alert('Please upload and parse a CSV file first.');
            return;
        }

        setIsProcessing(true);
        setUploadResult(null);

        try {
            const response = await ApiClient.post('/api/pricelists/bulk-update', parsedData);
            setUploadResult(response.data);
            
            // If successful, notify parent component
            if (response.data && response.data.errorCount === 0) {
                setTimeout(() => {
                    onComplete(response.data);
                }, 2000);
            }
        } catch (error) {
            console.error('Error during bulk update:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
            setUploadResult({
                error: true,
                message: errorMessage,
                updatedCount: 0,
                insertedCount: 0,
                errorCount: parsedData.length,
                totalProcessed: parsedData.length
            });
        } finally {
            setIsProcessing(false);
        }
    };

    const handleClose = () => {
        // Reset all state
        setCsvFile(null);
        setParsedData([]);
        setPreviewData([]);
        setUploadResult(null);
        setIsProcessing(false);
        setIsDragging(false);
        setDragCounter(0);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} className="relative z-50">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            <div className="fixed inset-0 z-10 overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
                        <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                            {/* Header */}
                            <div className="flex items-center justify-between">
                                <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                                    Bulk Update Price Lists
                                </DialogTitle>
                                <button
                                    type="button"
                                    className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                                    onClick={handleClose}
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>

                            {/* Instructions */}
                            <div className="mt-4 p-4 bg-blue-50 rounded-md">
                                <h4 className="text-sm font-medium text-blue-900 mb-2">CSV Upload Instructions:</h4>
                                <ul className="text-sm text-blue-800 space-y-1">
                                    <li>• Required columns: <strong>upc, MAP, item_code, product_title, cost</strong></li>
                                    <li>• UPC values will be matched against existing GTIN values in the database</li>
                                    <li>• Existing records will be updated, new records will be inserted</li>
                                    <li>• Supported formats: CSV, TXT files</li>
                                </ul>
                            </div>

                            {/* File Upload Area */}
                            <div className="mt-6">
                                <div
                                    className={`relative border-2 border-dashed rounded-lg p-6 ${
                                        isDragging
                                            ? 'border-indigo-400 bg-indigo-50'
                                            : 'border-gray-300 hover:border-gray-400'
                                    }`}
                                    onDragEnter={handleDragEnter}
                                    onDragLeave={handleDragLeave}
                                    onDragOver={preventDefaults}
                                    onDrop={handleDrop}
                                >
                                    <div className="text-center">
                                        <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-gray-400" />
                                        <div className="mt-4">
                                            <label htmlFor="file-upload" className="cursor-pointer">
                                                <span className="mt-2 block text-sm font-medium text-gray-900">
                                                    {csvFile ? csvFile.name : 'Drop CSV file here or click to browse'}
                                                </span>
                                                <input
                                                    id="file-upload"
                                                    name="file-upload"
                                                    type="file"
                                                    accept=".csv,.txt"
                                                    className="sr-only"
                                                    onChange={handleFileInput}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Preview Data */}
                            {previewData.length > 0 && (
                                <div className="mt-6">
                                    <h4 className="text-sm font-medium text-gray-900 mb-3">
                                        Preview (First 5 rows of {parsedData.length} total):
                                    </h4>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200 border">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    {requiredColumns.map((col) => (
                                                        <th key={col} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            {col}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {previewData.map((row, index) => (
                                                    <tr key={index}>
                                                        {requiredColumns.map((col) => (
                                                            <td key={col} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                                                                {row[col] || 'N/A'}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Upload Result */}
                            {uploadResult && (
                                <div className={`mt-6 p-4 rounded-md ${
                                    uploadResult.error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'
                                }`}>
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            {uploadResult.error ? (
                                                <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                                            ) : (
                                                <DocumentTextIcon className="h-5 w-5 text-green-400" />
                                            )}
                                        </div>
                                        <div className="ml-3">
                                            <h3 className={`text-sm font-medium ${
                                                uploadResult.error ? 'text-red-800' : 'text-green-800'
                                            }`}>
                                                {uploadResult.error ? 'Upload Failed' : 'Upload Completed Successfully'}
                                            </h3>
                                            <div className={`mt-2 text-sm ${
                                                uploadResult.error ? 'text-red-700' : 'text-green-700'
                                            }`}>
                                                {uploadResult.error ? (
                                                    <p>{uploadResult.message}</p>
                                                ) : (
                                                    <div>
                                                        <p>Processed {uploadResult.totalProcessed} records:</p>
                                                        <ul className="list-disc list-inside mt-1">
                                                            <li>{uploadResult.updatedCount} records updated</li>
                                                            <li>{uploadResult.insertedCount} records inserted</li>
                                                            {uploadResult.errorCount > 0 && (
                                                                <li className="text-red-600">{uploadResult.errorCount} errors occurred</li>
                                                            )}
                                                        </ul>
                                                        {uploadResult.errors && uploadResult.errors.length > 0 && (
                                                            <div className="mt-2">
                                                                <p className="font-medium">Errors:</p>
                                                                <ul className="list-disc list-inside text-xs">
                                                                    {uploadResult.errors.slice(0, 5).map((error, index) => (
                                                                        <li key={index}>{error}</li>
                                                                    ))}
                                                                    {uploadResult.errors.length > 5 && (
                                                                        <li>... and {uploadResult.errors.length - 5} more errors</li>
                                                                    )}
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                            <button
                                type="button"
                                disabled={!parsedData.length || isProcessing}
                                onClick={handleSubmit}
                                className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
                            >
                                {isProcessing ? 'Processing...' : `Update ${parsedData.length} Records`}
                            </button>
                            <button
                                type="button"
                                onClick={handleClose}
                                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                            >
                                Cancel
                            </button>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}